// Support.jsx
import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Search, MessageCircle, Clock, CheckCircle2, AlertCircle, Tag, ChevronRight,
  X, Send, Bot, UserCheck, Trash2,
} from 'lucide-react'
import { ConfirmModal } from '../../common/Modal/Modal'
import {
  fetchTickets,
  fetchTicket,
  replyTicket,
  updateTicket,
  deleteTicket,
  selectTicket,
  clearActive,
} from './services/supportSlice'

const PRIORITY = {
  urgent: { cls:'badge-danger',  l:'Urgent', dot:'#EF4444' },
  high:   { cls:'badge-warning', l:'High',   dot:'#F59E0B' },
  medium: { cls:'badge-info',    l:'Medium', dot:'#06B6D4' },
  low:    { cls:'badge-active',  l:'Low',    dot:'#10B981' },
}

const STATUS = {
  open:          { cls:'badge-warning', l:'Open',        icon:AlertCircle,  color:'#F59E0B' },
  'in-progress': { cls:'badge-info',    l:'In Progress', icon:Clock,        color:'#06B6D4' },
  resolved:      { cls:'badge-active',  l:'Resolved',    icon:CheckCircle2, color:'#10B981' },
}

const POLL_MS = 8000

const fmtTime = (t) =>
  t ? new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''

const initials = (name = '') => name.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase()

export default function Support() {
  const dispatch = useDispatch()
  const { list: tickets, active, messages, isLoading, isSaving, isError, message } =
    useSelector((state) => state.support)

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [reply, setReply]   = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)

  const endRef = useRef(null)
  const activeId = active?.id

  /* ── load + polling (list + open ticket) ── */
  useEffect(() => {
    dispatch(fetchTickets())
    return () => { dispatch(clearActive()) }
  }, [dispatch])

  useEffect(() => {
    const t = setInterval(() => {
      if (document.hidden) return
      dispatch(fetchTickets({ silent: true }))
      if (activeId) dispatch(fetchTicket({ id: activeId, silent: true }))
    }, POLL_MS)
    return () => clearInterval(t)
  }, [dispatch, activeId])

  // new message aave to niche scroll
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages.length, activeId])

  const openTicket = (t) => {
    dispatch(selectTicket(t))
    dispatch(fetchTicket({ id: t.id }))
  }

  const filtered = tickets.filter((t) => {
    const q = search.toLowerCase()
    const matchStatus = filter === 'all' || t.status === filter
    const matchSearch = !q ||
      t.subject.toLowerCase().includes(q) ||
      t.user.toLowerCase().includes(q) ||
      t.ticket_no.toLowerCase().includes(q)
    return matchStatus && matchSearch
  })

  const count = (s) => tickets.filter((t) => t.status === s).length
  const needsYou = tickets.filter((t) => t.needs_admin && t.status !== 'resolved').length

  const stats = [
    { label:'Total Tickets', value:tickets.length,    color:'#4F46E5', colorGlow:'rgba(79,70,229,0.18)',  icon:MessageCircle, variant:'indigo', trend:`${needsYou} need you` },
    { label:'Open',          value:count('open'),        color:'#F59E0B', colorGlow:'rgba(245,158,11,0.15)',  icon:AlertCircle,   variant:'amber',  trend:'new' },
    { label:'In Progress',   value:count('in-progress'), color:'#06B6D4', colorGlow:'rgba(6,182,212,0.15)',   icon:Clock,         variant:'cyan',   trend:'active' },
    { label:'Resolved',      value:count('resolved'),    color:'#10B981', colorGlow:'rgba(16,185,129,0.15)',  icon:CheckCircle2,  variant:'green',  trend:'done' },
  ]

  const update = (data) => dispatch(updateTicket({ id: active.id, ...data }))

  const sendReply = async () => {
    if (!reply.trim() || !active || isSaving) return
    const result = await dispatch(replyTicket({ id: active.id, message: reply.trim() }))
    if (result.meta.requestStatus === 'fulfilled') setReply('')
  }

  const onDelete = async () => {
    const result = await dispatch(deleteTicket(active.id))
    if (result.meta.requestStatus === 'fulfilled') setDeleteOpen(false)
  }

  return (
    <div className="dash-page page-in">

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'var(--space-md)' }}>
        <h1 className="page-title">Support</h1>
      </div>

      {/* Stats */}
      <div className="stats-grid cols-4">
        {stats.map((s) => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{
              position:'absolute', top:0, right:0, width:110, height:110, borderRadius:'50%',
              background:s.colorGlow, filter:'blur(30px)', transform:'translate(30%, -30%)', pointerEvents:'none',
            }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background:s.colorGlow, color:s.color, border:`1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color:s.color, background:`${s.color}15`, border:`1px solid ${s.colorGlow}` }}>
                {s.trend}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{
              position:'absolute', bottom:0, left:0, right:0, height:2,
              background:`linear-gradient(90deg,${s.color}88,transparent)`,
              borderRadius:'0 0 var(--radius-lg) var(--radius-lg)',
            }} />
          </div>
        ))}
      </div>

      {/* Main shell — Gmail-style split */}
      <div className={`sup-shell${active ? ' sup-shell--open' : ''}`}>

        {/* Left — ticket list */}
        <div className="sup-list">
          <div className="sup-toolbar">
            <div style={{ position:'relative', flex:1 }}>
              <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text3)' }}/>
              <input className="input" style={{ paddingLeft:32, height:34, fontSize:'var(--fs-xs)' }} placeholder="Search tickets…" value={search} onChange={(e) => setSearch(e.target.value)}/>
            </div>
            <div className="view-tabs" style={{ margin:0 }}>
              {['all','open','in-progress','resolved'].map((f) => (
                <button key={f} className={`vtab${filter===f?' active':''}`} style={{ padding:'5px 10px', fontSize:'var(--fs-xs)' }} onClick={() => setFilter(f)}>
                  {f==='in-progress' ? 'Active' : f.charAt(0).toUpperCase()+f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="sup-list-body">
            {isLoading && <div style={{ textAlign:'center', padding:'2rem', color:'var(--text3)', fontSize:'var(--fs-xs)' }}>Loading…</div>}
            {!isLoading && filtered.length === 0 && (
              <div style={{ textAlign:'center', padding:'2rem', color:'var(--text3)', fontSize:'var(--fs-xs)' }}>No tickets found</div>
            )}
            {filtered.map((t) => {
              const S = STATUS[t.status]
              const P = PRIORITY[t.priority]
              const isActive = active?.id === t.id
              return (
                <div key={t.id} className={`sup-list-item${isActive?' sup-list-item--active':''}`} onClick={() => openTicket(t)}>
                  <span style={{ width:8, height:8, borderRadius:'50%', background:P.dot, flexShrink:0, marginTop:4 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:6, marginBottom:3 }}>
                      <span style={{ fontSize:'var(--fs-xs)', fontWeight:t.unread>0?800:700, color:isActive?'var(--accent-hover)':'var(--text1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</span>
                      <span style={{ fontSize:10, color:'var(--text4)', flexShrink:0 }}>{t.date?.split(',')[0]}</span>
                    </div>
                    <div style={{ fontSize:10, color:'var(--text3)', marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.user} · {t.email}</div>
                    {t.last_message && (
                      <div style={{ fontSize:10, color:'var(--text4)', marginBottom:5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.last_message}</div>
                    )}
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap', alignItems:'center' }}>
                      <span className="badge badge-gold" style={{ fontSize:9, padding:'1px 6px' }}>{t.category}</span>
                      <span className={`badge ${S.cls}`} style={{ fontSize:9, padding:'1px 6px' }}>{S.l}</span>
                      <span className={`badge ${P.cls}`} style={{ fontSize:9, padding:'1px 6px' }}>{P.l}</span>
                      {t.status !== 'resolved' && (
                        t.needs_admin
                          ? <span className="badge badge-danger" style={{ fontSize:9, padding:'1px 6px' }}>Needs you</span>
                          : t.handler === 'ai'
                            ? <span className="badge badge-info" style={{ fontSize:9, padding:'1px 6px' }}>AI handling</span>
                            : null
                      )}
                      {t.unread > 0 && (
                        <span style={{ marginLeft:'auto', minWidth:16, height:16, borderRadius:8, padding:'0 5px', background:'var(--accent)', color:'#fff', fontSize:9, fontWeight:700, display:'grid', placeItems:'center' }}>{t.unread}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={14} color="var(--text4)" style={{ flexShrink:0, marginTop:2 }}/>
                </div>
              )
            })}
          </div>
        </div>

        {/* Right — ticket detail */}
        <div className="sup-detail">
          {active ? (
            <>
              <div className="sup-detail-header">
                <button className="sup-back-btn" onClick={() => dispatch(clearActive())}>
                  <ChevronRight size={15} style={{ transform:'rotate(180deg)' }}/> Back
                </button>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, color:'var(--text1)', fontSize:'var(--fs-md)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{active.subject}</div>
                  <div style={{ fontSize:'var(--fs-xs)', color:'var(--text3)', marginTop:2 }}>{active.ticket_no} · {active.date}</div>
                </div>
                <button className="btn btn-ghost btn-icon" style={{ width:28, height:28 }} title="Delete ticket" onClick={() => setDeleteOpen(true)}><Trash2 size={14}/></button>
                <button className="btn btn-ghost btn-icon" style={{ width:28, height:28 }} onClick={() => dispatch(clearActive())}><X size={14}/></button>
              </div>

              {/* Meta badges + controls */}
              <div style={{ padding:'10px 18px', borderBottom:'1px solid var(--border-subtle)', display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                <span className={`badge ${STATUS[active.status].cls}`} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  {(() => { const Icon = STATUS[active.status].icon; return <Icon size={10}/> })()}
                  {STATUS[active.status].l}
                </span>
                <span className={`badge ${PRIORITY[active.priority].cls}`}>{PRIORITY[active.priority].l}</span>
                <span className="badge badge-gold"><Tag size={10}/> {active.category}</span>
                <span className={`badge ${active.handler === 'ai' ? 'badge-info' : 'badge-active'}`} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  {active.handler === 'ai' ? <><Bot size={10}/> AI handling</> : <><UserCheck size={10}/> Admin handling</>}
                </span>

                <div style={{ marginLeft:'auto', display:'flex', gap:6, flexWrap:'wrap' }}>
                  {active.handler === 'ai' ? (
                    <button className="btn btn-outline btn-sm" disabled={isSaving} onClick={() => update({ handler:'admin' })}>Take over</button>
                  ) : (
                    <button className="btn btn-outline btn-sm" disabled={isSaving} onClick={() => update({ handler:'ai' })}>Hand back to AI</button>
                  )}
                  <select className="input" style={{ width:'auto', height:28, fontSize:'var(--fs-xs)', padding:'0 8px' }}
                    value={active.priority} onChange={(e) => update({ priority: e.target.value })}>
                    {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                  </select>
                  <select className="input" style={{ width:'auto', height:28, fontSize:'var(--fs-xs)', padding:'0 8px' }}
                    value={active.status} onChange={(e) => update({ status: e.target.value })}>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                  </select>
                </div>
              </div>

              {/* User info */}
              <div style={{ padding:'12px 18px', borderBottom:'1px solid var(--border-subtle)', display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--bg-active)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'var(--fs-xs)', fontWeight:700, color:'var(--accent-hover)', flexShrink:0 }}>
                  {initials(active.user)}
                </div>
                <div>
                  <div style={{ fontWeight:600, color:'var(--text1)', fontSize:'var(--fs-sm)' }}>{active.user}</div>
                  <div style={{ fontSize:'var(--fs-xs)', color:'var(--text3)' }}>{active.email}</div>
                </div>
                <div style={{ marginLeft:'auto', textAlign:'right' }}>
                  <div style={{ fontSize:'var(--fs-xs)', color:'var(--text3)' }}>Messages</div>
                  <div style={{ fontSize:'var(--fs-sm)', fontWeight:700, color:'var(--accent-hover)' }}>{messages.length || active.msgs}</div>
                </div>
              </div>

              {isError && message && (
                <div className="login-error" style={{ margin:'10px 18px 0' }}>{message}</div>
              )}

              {/* Messages */}
              <div className="sup-messages">
                {messages.length === 0 && (
                  <div style={{ textAlign:'center', color:'var(--text4)', fontSize:'var(--fs-xs)', padding:'1rem' }}>Loading messages…</div>
                )}
                {messages.map((m) => {
                  const fromUser = m.sender === 'customer'
                  const isAi = m.sender === 'ai'
                  return (
                    <div key={m.id} className={`sup-msg sup-msg--${fromUser ? 'user' : 'agent'}`}>
                      {fromUser && (
                        <div style={{ width:26, height:26, borderRadius:'50%', background:'var(--bg-active)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'var(--fs-xs)', fontWeight:700, color:'var(--accent-hover)', flexShrink:0 }}>
                          {initials(active.user)}
                        </div>
                      )}
                      <div className={`sup-bubble sup-bubble--${fromUser ? 'user' : 'agent'}${isAi ? ' sup-bubble--ai' : ''}`}>
                        {isAi && <div style={{ fontSize:9, fontWeight:700, opacity:.8, marginBottom:3, display:'flex', alignItems:'center', gap:4 }}><Bot size={10}/> AI assistant</div>}
                        <div style={{ fontSize:'var(--fs-xs)', lineHeight:1.6, whiteSpace:'pre-wrap' }}>{m.text}</div>
                        <div style={{ fontSize:9, opacity:0.6, marginTop:4, textAlign:fromUser?'left':'right' }}>{fmtTime(m.time)}</div>
                      </div>
                      {!fromUser && (
                        <div style={{ width:26, height:26, borderRadius:'50%', background:isAi?'#64748b':'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'var(--fs-xs)', fontWeight:700, color:'#fff', flexShrink:0 }}>
                          {isAi ? <Bot size={13}/> : 'A'}
                        </div>
                      )}
                    </div>
                  )
                })}
                <div ref={endRef} />
              </div>

              {/* Reply box */}
              <div className="sup-reply">
                <textarea
                  className="sup-reply-input"
                  placeholder={active.handler === 'ai' ? 'AI is replying… your reply will take over this chat' : 'Type your reply… (Ctrl+Enter to send)'}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendReply() }}
                  rows={2}
                />
                <button className="btn btn-primary btn-sm" style={{ flexShrink:0 }} disabled={!reply.trim() || isSaving} onClick={sendReply}>
                  <Send size={13}/> Send
                </button>
              </div>
            </>
          ) : (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, color:'var(--text4)' }}>
              <MessageCircle size={44} strokeWidth={1}/>
              <div style={{ fontSize:'var(--fs-sm)', fontWeight:500 }}>Select a ticket to view</div>
              <div style={{ fontSize:'var(--fs-xs)', color:'var(--text4)' }}>{filtered.length} tickets in this view</div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Delete ticket"
        message={`Delete ${active?.ticket_no || 'this ticket'} and all its messages?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }
        .login-error {
          background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5; font-size: 0.875rem; padding: 0.6rem 0.9rem; border-radius: 10px;
        }

        .sup-shell {
          display: grid; grid-template-columns: 340px 1fr; height: 80vh;
          border: 1px solid var(--border); border-radius: var(--radius-lg);
          overflow: hidden; background: var(--bg-card); box-shadow: 0 20px 50px rgba(0,0,0,0.12);
        }
        .sup-list { display:flex; flex-direction:column; border-right:1px solid var(--border-subtle); overflow:hidden; background:var(--bg-surface); }
        .sup-toolbar { display:flex; flex-direction:column; gap:8px; padding:10px 12px; border-bottom:1px solid var(--border-subtle); flex-shrink:0; }
        .sup-list-body { flex:1; overflow-y:auto; }
        .sup-list-item {
          display:flex; align-items:flex-start; gap:8px; padding:12px 14px; cursor:pointer;
          border-bottom:1px solid var(--border-subtle); border-left:3px solid transparent;
          transition: background .15s, border-color .15s;
        }
        .sup-list-item:hover { background: var(--bg-hover); }
        .sup-list-item--active { background: var(--bg-active); border-left-color: var(--accent-hover); }

        .sup-detail { display:flex; flex-direction:column; overflow:hidden; background:var(--bg-raised); }
        .sup-detail-header { display:flex; align-items:center; gap:10px; padding:12px 18px; border-bottom:1px solid var(--border-subtle); flex-shrink:0; }
        .sup-back-btn {
          display:none; align-items:center; gap:4px; background:none; border:none; cursor:pointer;
          color:var(--accent-hover); font-size:var(--fs-xs); font-weight:600; font-family:inherit; padding:4px 0;
        }

        .sup-messages { flex:1; overflow-y:auto; padding:16px 18px; display:flex; flex-direction:column; gap:12px; }
        .sup-msg { display:flex; gap:8px; align-items:flex-end; }
        .sup-msg--agent { flex-direction: row-reverse; }
        .sup-bubble { max-width:75%; padding:10px 14px; border-radius:14px; font-family:var(--font-body); }
        .sup-bubble--user { background:var(--bg-card); border:1px solid var(--border); border-bottom-left-radius:4px; color:var(--text1); }
        .sup-bubble--agent { background:var(--accent); border-bottom-right-radius:4px; color:#fff; }
        .sup-bubble--ai { background:#475569; }

        .sup-reply { display:flex; gap:8px; align-items:flex-end; padding:12px 16px; border-top:1px solid var(--border-subtle); flex-shrink:0; }
        .sup-reply-input {
          flex:1; background:var(--bg-card); border:1px solid var(--border); border-radius:var(--radius-md);
          color:var(--text1); font-family:var(--font-body); font-size:var(--fs-sm); padding:8px 12px;
          outline:none; resize:none; line-height:1.5; transition:border-color .2s;
        }
        .sup-reply-input:focus { border-color: var(--accent); }
        .sup-reply-input::placeholder { color: var(--text3); }

        @media (max-width: 900px) {
          .sup-shell { grid-template-columns: 1fr; height: auto; }
          .sup-detail { display: none; min-height: 500px; }
          .sup-shell.sup-shell--open .sup-list { display: none; }
          .sup-shell.sup-shell--open .sup-detail { display: flex; }
          .sup-back-btn { display: flex !important; }
        }
        @media (max-width: 480px) {
          .sup-toolbar { gap: 6px; }
          .view-tabs .vtab { padding: 4px 8px; }
        }
      `}</style>
    </div>
  )
}